import { useContext, useEffect, useState } from "react";

import ConfirmationPopup from "../../../../shared/components/confirmation-popup/confirmation-popup";
import Spinner from "../../../../shared/components/spinner/spinner";
import AuthContext from "../../../../shared/context/auth-context";
import useHttp from "../../../../shared/hooks/use-http";
import { getAllData, postSingleNonTableImport } from "../../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../../shared/utils/utils";
import ControlledTable from "../../../components/controlled-table/controlled-table";
import { ISortedBy, ITableRowData } from "../../../components/data-table/data-table";
import { IColumn } from "../../../components/data-table/data-table";
import Popup from "../../../components/popup/popup";
import { noop } from "../../../utils";
import DocumentModal from "../documents/document-modal";
import { notesColumns } from "../notes/notes";
import SkipModal from "./skip-modal";

const actionColumns: IColumn[] = [{
  key: "name",
  displayName: "Action",
  dataType: {
    type: "text"
  }
}, {
  key: "action_type",
  displayName: "Action type",
  dataType: {
    type: "text"
  }
}, {
  key: "to_perform",
  displayName: "To perform",
  dataType: {
    type: "text"
  }
}, {
  key: "performed_by_user",
  displayName: "Performed by",
  sortable: false,
  dataType: {
    type: "text"
  }
}, {
  key: "performed_at",
  displayName: "Performed at",
  dataType: {
    type: "dateTime"
  }
},
{
  key: "performed",
  displayName: "Action performed",
  dataType: {
    type: "text"
  }
},
{
  key: "skip_action",
  displayName: "Skip action",
  sortable: false,
  dataType: {
    type: "action",
    buttonText: "Skip",
    showCondition: "recommended",
    displayName: (rowData: ITableRowData) => (rowData["skipped"])? "Skipped" : "Skip",
    disabled: (rowData: ITableRowData) => !!rowData["skipped"] || !!rowData["performed"]
  }
}
];

interface IActionsProps {
  alertcaseId: string;
  onActionPerformed: () => void;
}

const Actions = ({ alertcaseId, onActionPerformed }: IActionsProps) => {
  const authCtx = useContext(AuthContext);
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({ column: "performed", direction: "asc" });
  const [data, setData] = useState<ITableRowData[]>([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showCheckmarkModal, setShowCheckmarkModal] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState<string>("");
  const [showSkipModal, setShowSkipModal] = useState(false);

  const { sendRequest: getTableData, status: getTableDataStatus, data: getTableDataResult, error: getTableDataError } = useHttp(getAllData, true, authCtx.token, "action", "manager");
  const { sendRequest: actionPerformed, status: actionPerformedStatus, data: actionPerformedResult, error: actionPerformedError } = useHttp(postSingleNonTableImport, false, authCtx.token, "action", "manager");
  const { sendRequest: postDocument, status: postDocumentStatus, error: postDocumentError } = useHttp(postSingleNonTableImport, false, authCtx.token, "default", "manager");

  const [isUserReadOnly, setIsUserReadOnly] = useState(false);
  useEffect(() => {
    const roles = authCtx.selectedTenant.roles || [];
    setIsUserReadOnly(roles.length === 1 && roles[0] === "Read");
  }, [authCtx.selectedTenant]);

  useEffect(() => {
    getTableData(`alertcase/${alertcaseId}/actions?order_by=${sortedByCol.column} ${sortedByCol.direction}`);
  }, [sortedByCol]);

  useEffect(() => {
    if (getTableDataStatus === "completed" && !getTableDataError && getTableDataResult) {
      setData(getTableDataResult);
    }
  }, [getTableDataStatus, getTableDataError, getTableDataResult]);

  useEffect(() => {
    if (actionPerformedStatus === "completed" && !actionPerformedError && actionPerformedResult) {
      setData(actionPerformedResult);
      onActionPerformed();
    }
  }, [actionPerformedStatus, actionPerformedError, actionPerformedResult]);

  useEffect(() => {
    if (postDocumentStatus === "completed" && !postDocumentError) {
      actionPerformed(`alertcase/${alertcaseId}/actions?order_by=${sortedByCol.column} ${sortedByCol.direction}`, { id: selectedActionId, is_skipped: false });
    }
  }, [postDocumentStatus, postDocumentError]);

  const handleOnRowClick = ((id: string) => {
    if (isUserReadOnly) {
      return;
    }
    const rowClicked = data.find(row => row.id === id);
    if (!rowClicked || rowClicked.performed) {
      return;
    }

    setSelectedActionId(id);
    if (rowClicked.to_perform === "document") {
      setShowDocumentModal(true);
    }
    if (rowClicked.to_perform === "note") {
      setShowNoteModal(true);
    }
    if (rowClicked.to_perform === "checkmark") {
      setShowCheckmarkModal(true);
    }
  });

  // Document Modal
  const handleDocumentModalSubmit = (files: File[]) => {
    if (files.length === 0 || isUserReadOnly) {
      setShowDocumentModal(false);
      return;
    }

    const formData = new FormData();
    files.forEach(file => formData.append("files", file));

    postDocument(`alertcase/${alertcaseId}/documents`, formData, "form");
    setShowDocumentModal(false);
  };

  // Note Modal
  const { sendRequest: postNote, status: postNoteStatus } = useHttp(postSingleNonTableImport, false, authCtx.token, "default", "manager");

  const handleNoteModalSubmit = (result: ITableRowData | undefined) => {
    if (isUserReadOnly) {
      return;
    }
    setShowNoteModal(false);
    if (!result) {
      return;
    }
    postNote(`alertcase/${alertcaseId}/notes`, result);
    actionPerformed(`alertcase/${alertcaseId}/actions?order_by=${sortedByCol.column} ${sortedByCol.direction}`, { id: selectedActionId, is_skipped: false });
  };

  // Checkmark Modal
  const handleCheckmarkModalSubmit = () => {
    if (isUserReadOnly) {
      return;
    }
    setShowCheckmarkModal(false);
    actionPerformed(`alertcase/${alertcaseId}/actions?order_by=${sortedByCol.column} ${sortedByCol.direction}`, { id: selectedActionId, is_skipped: false });
  };

  const handleActionClick = (action_key: string, rowId: string) => {
    if (isUserReadOnly) {
      return;
    }
    setSelectedActionId(rowId);
    if (action_key === "skip_action") {
      setShowSkipModal(true);
    }
  };

  return <div className="actions">
    {validateLoadingStatusNew([postDocumentStatus, getTableDataStatus, postNoteStatus, actionPerformedStatus]) && <Spinner />}
    <DocumentModal showModal={showDocumentModal} onModalSubmit={handleDocumentModalSubmit} onModalClosed={() => setShowDocumentModal(false)} />

    <Popup
      mode={"Note"}
      buttonText={"Save"}
      columns={notesColumns}
      values={undefined}
      show={showNoteModal}
      handlePopupClosed={handleNoteModalSubmit}
    />

    <ConfirmationPopup
      confirmButtonText={"Yes"}
      text={"Have you performed this action?"}
      show={showCheckmarkModal}
      handlePopupClosed={() => setShowCheckmarkModal(false)}
      handleSaveClick={handleCheckmarkModalSubmit}
    />
    <ControlledTable data={data} onSorted={setSortedByCol} onRowClick={handleOnRowClick} onScroll={noop} selectionMode="off" columns={actionColumns} onActionClick={handleActionClick} />

    <SkipModal
      show={showSkipModal}
      onHide={() => setShowSkipModal(false)}
      onSkip={(note: string) => actionPerformed(`alertcase/${alertcaseId}/actions?order_by=${sortedByCol.column} ${sortedByCol.direction}`, { id: selectedActionId, is_skipped: true, skip_note: note })}
    />

  </div>;
};

export default Actions;